import React, { useEffect, useMemo, useRef, useState } from "react";
import StockMovementTable from "../components/StockMovementTable";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

// The Movement type must match backend output exactly!
type Movement = {
  _id: string;
  item: { name: string; sku: string };
  type: "in" | "out" | "adjustment";
  quantity: number;              // mapped from backend .qty
  reason?: string;               // mapped from backend .note or .reason
  createdBy: { name: string };
  createdAt: string;
};

const MovementLogPage: React.FC = () => {
  useProtectedRoute();
  const { token } = useAuth();

  const [movements, setMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [compact, setCompact] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    api
      .get(`/inventory/movements${params.toString() ? `?${params.toString()}` : ''}` , {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // If your backend returns "qty" and "note", map to "quantity" and "reason" for frontend table
        const mapped: Movement[] = res.data.map((m: any) => ({
          _id: m._id,
          item: m.item,
          type: m.type,
          quantity: m.qty,           // <-- backend sends .qty
          reason: m.note || m.reason || "",
          createdBy: m.createdBy,
          createdAt: m.createdAt,
        }));
        setMovements(mapped);
      })
      .finally(() => setLoading(false));
  }, [token, type, from, to]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return movements;
    return movements.filter(m =>
      [m.item?.name, m.item?.sku, m.type, m.reason, m.createdBy?.name]
        .filter(Boolean).join(' ').toLowerCase().includes(term)
    );
  }, [movements, q]);

  return (
    <div className="max-w-6xl mx-auto p-4" ref={scrollRef}>
      <h1 className="text-2xl font-extrabold mb-4 text-white">Inventory Movement Log</h1>

      <div className="bg-white rounded-xl shadow mb-3 p-3" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <input className="border rounded px-3 py-2 col-span-2" placeholder="Search name / SKU / reason / by" value={q} onChange={e=>setQ(e.target.value)} />
          <select className="border rounded px-3 py-2" value={type} onChange={e=>setType(e.target.value)}>
            <option value="">All types</option>
            <option value="in">In</option>
            <option value="out">Out</option>
            <option value="adjustment">Adjustment</option>
          </select>
          <input className="border rounded px-3 py-2" type="date" value={from} onChange={e=>setFrom(e.target.value)} />
          <input className="border rounded px-3 py-2" type="date" value={to} onChange={e=>setTo(e.target.value)} />
          <label className="flex items-center gap-2 text-slate-700"><input type="checkbox" checked={compact} onChange={e=>setCompact(e.target.checked)} /> Compact</label>
        </div>
      </div>

      <StockMovementTable movements={filtered} loading={loading} compact={compact} />

      {/* Scroll helpers */}
      <div className="fixed right-5 bottom-5 flex flex-col gap-2">
        <button className="bg-blue-600 text-white rounded-full w-10 h-10 shadow hover:bg-blue-700" onClick={()=>window.scrollTo({ top: 0, behavior: 'smooth' })}>↑</button>
        <button className="bg-blue-600 text-white rounded-full w-10 h-10 shadow hover:bg-blue-700" onClick={()=>window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>↓</button>
      </div>
    </div>
  );
};

export default MovementLogPage;
